import type { AxiosError } from 'axios';
import axios from 'axios';
import { z } from 'zod/v4';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { APIStatus, OrgSchema } from '../../../enum';
import { removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

const CommunityLabelSchema = z.object({
  id: z.string(),
  name: z.string(),
  total_count: z.number(),
  org_name: z.string(),
  org_code: OrgSchema,
  preference_count: z.number(),
});

export type AdminCommunityLabel = z.infer<typeof CommunityLabelSchema>;

type AdminCommunitySliceType = {
  adminCommunityLabels: AdminCommunityLabel[];
  requestAdminCommunitiesParam: RequestParams<AdminCommunityLabel>;
  adminCommunityLabelsApiStatus: APIStatus;
};

const initialState: AdminCommunitySliceType = {
  adminCommunityLabels: [],
  requestAdminCommunitiesParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<AdminCommunityLabel>('preference_count', 'descend'),
    filteredInfo: {},
  },
  adminCommunityLabelsApiStatus: APIStatus.IDLE,
};

export const fetchAdminCommunityLabels = createAsyncThunk<AdminCommunityLabel[], undefined, AsyncThunkConfig>(
  'adminCommunity/fetchAdminCommunityLabels',
  async (_, thunkAPI) => {
    try {
      const requestAdminCommunitiesParam = thunkAPI.getState().adminCommunitySlice.requestAdminCommunitiesParam;
      const response = (await axios.post(
        'v0_get_community_labels',
        requestAdminCommunitiesParam,
      )) as AdminCommunityLabel[];
      const result = z.array(CommunityLabelSchema).safeParse(response);
      if (result.error) {
        logError(result.error, ['safeParse', 'adminCommunitySlice', 'fetchAdminCommunityLabels']);
      }
      return response ?? [];
    } catch (e) {
      logError(e, ['adminCommunitySlice', 'fetchAdminCommunityLabels']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const adminCommunitySlice = createSlice({
  name: 'adminCommunity',
  initialState,
  reducers: {
    handleCommunitiesTableChange: (state, action: PayloadAction<TableChange<AdminCommunityLabel>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<AdminCommunityLabel>;
      state.requestAdminCommunitiesParam.pagination = action.payload.pagination;
      state.requestAdminCommunitiesParam.sortedInfo = sorter;
      state.requestAdminCommunitiesParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestAdminCommunitiesParam.pagination = initialState.requestAdminCommunitiesParam.pagination;
      state.requestAdminCommunitiesParam.sortedInfo = initialState.requestAdminCommunitiesParam.sortedInfo;
      state.requestAdminCommunitiesParam.filteredInfo = initialState.requestAdminCommunitiesParam.filteredInfo;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminCommunityLabels.pending, (state, _action) => {
        state.adminCommunityLabelsApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchAdminCommunityLabels.fulfilled, (state, action) => {
        state.adminCommunityLabels = action.payload;
        state.adminCommunityLabelsApiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchAdminCommunityLabels.rejected, (state, _action) => {
        state.adminCommunityLabelsApiStatus = APIStatus.ERROR;
      });
  },
});

export const { handleCommunitiesTableChange, clearFilter } = adminCommunitySlice.actions;
